import React, { useEffect, useRef } from 'react';
import { addToast } from '@heroui/react';
import {
  getToastPlacementFromAnchorOrigin,
  mapSeverityToHeroTone,
} from './toastTheme';

interface SnackbarProps {
  open: boolean;
  handleClose: () => void;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'start' | 'end';
  };
}

const capitalizeFirstLetter = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const CustomSnackbar = React.forwardRef<HTMLDivElement, SnackbarProps>(
  (
    { open, handleClose, message, severity, duration = 3000, anchorOrigin },
    ref
  ) => {
    const lastToastKeyRef = useRef<string | null>(null);

    useEffect(() => {
      if (!open || !message) {
        lastToastKeyRef.current = null;
        return;
      }

      const toastKey = `${severity}:${message}:${duration}`;

      if (lastToastKeyRef.current === toastKey) return;

      lastToastKeyRef.current = toastKey;

      const toastPlacement = getToastPlacementFromAnchorOrigin(anchorOrigin);

      addToast({
        title: capitalizeFirstLetter(message),
        color: mapSeverityToHeroTone(severity),
        severity: mapSeverityToHeroTone(severity),
        timeout: duration,
        ...(toastPlacement ? { placement: toastPlacement } : {}),
        onClose: () => {
          lastToastKeyRef.current = null;
          handleClose();
        },
      });
    }, [open, message, severity, duration, handleClose, anchorOrigin]);

    return <div ref={ref} style={{ display: 'none' }} />;
  }
);

export default CustomSnackbar;
